/**
 * Client diagnostics attached to a feedback report.
 * Headers, tokens, cookies, and email addresses are removed before they are kept.
 */

const MAX_ITEMS = 8
const MAX_LENGTH = 500

const SECRET_RE =
  /(?:authorization\s*[:=]\s*bearer\s+\S+)|(?:bearer\s+[A-Za-z0-9\-._~+/]{8,}={0,2})|(?:cookie\s*[:=]\s*\S+)|(?:(?:password|passwd|secret|token|api[_-]?key|access[_-]?token|refresh[_-]?token)\s*[:=]\s*\S+)|(?:eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,})|(?:ghp_[A-Za-z0-9]{8,})|(?:github_pat_[A-Za-z0-9_]{8,})/gi

const QUERY_SECRET_RE =
  /([?&](?:token|password|secret|access_token|refresh_token|code|authorization)=)[^&\s]+/gi

const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g

export interface FailedRequest {
  method: string
  path: string
  status: number
}

export interface FeedbackDiagnostics {
  console_errors: string[]
  failed_requests: FailedRequest[]
}

const consoleErrors: string[] = []
const failedRequests: FailedRequest[] = []
let installed = false

/** Role text for the send-feedback dialog. Non-Erudio viewers never see tier wording. */
export function dialogRoleLabel(roleLabel: string | null | undefined): string {
  if (!roleLabel) return '…'
  if (roleLabel === 'Erudio admin') return 'Erudio admin'
  if (roleLabel === 'User') return 'User'
  return 'Admin'
}

export function sanitizeDiagnostic(value: string, limit = MAX_LENGTH): string {
  const text = String(value || '')
    .split(String.fromCharCode(0))
    .join('')
    .replace(SECRET_RE, '[redacted]')
    .replace(QUERY_SECRET_RE, '$1[redacted]')
    .replace(EMAIL_RE, '[redacted-email]')
    .replace(/```/g, "'''")
    .replace(/\s+/g, ' ')
    .trim()
  if (text.length <= limit) return text
  return text.slice(0, limit - 1).trimEnd() + '…'
}

export function safePath(url: string): string {
  const raw = String(url || '')
  try {
    const parsed = new URL(raw, 'http://local.invalid')
    return (parsed.pathname || '/').slice(0, 200)
  } catch {
    const path = raw.split('?')[0] || '/'
    return path.startsWith('/') ? path.slice(0, 200) : `/${path}`.slice(0, 200)
  }
}

function remember(list: string[], value: string) {
  const clean = sanitizeDiagnostic(value)
  if (!clean || clean === '[redacted]') return
  list.push(clean)
  if (list.length > MAX_ITEMS) list.shift()
}

export function noteConsoleError(message: string) {
  remember(consoleErrors, message)
}

export function noteFailedRequest(method: string, url: string, status: number) {
  const verb = String(method || 'GET').toUpperCase()
  const path = sanitizeDiagnostic(safePath(url), 200) || '/'
  failedRequests.push({
    method: /^[A-Z]{3,7}$/.test(verb) ? verb : 'GET',
    path,
    status: Number.isFinite(status) ? Math.max(0, Math.min(599, Math.trunc(status))) : 0,
  })
  if (failedRequests.length > MAX_ITEMS) failedRequests.shift()
}

export function snapshotDiagnostics(): FeedbackDiagnostics {
  return {
    console_errors: [...consoleErrors],
    failed_requests: failedRequests.map((item) => ({ ...item })),
  }
}

export function resetDiagnostics() {
  consoleErrors.length = 0
  failedRequests.length = 0
}

function describe(value: unknown): string {
  if (value instanceof Error) return value.message
  if (typeof value === 'string') return value
  if (value && typeof value === 'object' && 'config' in value) return 'request failed'
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

export function installFeedbackDiagnostics() {
  if (installed || typeof window === 'undefined') return
  installed = true
  const original = console.error.bind(console)
  console.error = (...args: unknown[]) => {
    noteConsoleError(args.map(describe).join(' '))
    original(...args)
  }
  window.addEventListener('error', (event) => {
    noteConsoleError(event.message || 'Script error')
  })
  window.addEventListener('unhandledrejection', (event) => {
    noteConsoleError(describe(event.reason))
  })
}

export async function frontendVersion(): Promise<string> {
  try {
    const response = await fetch('/version.json')
    if (!response.ok) return 'dev'
    const body = (await response.json()) as { version?: unknown }
    return typeof body.version === 'string' && body.version ? body.version : 'dev'
  } catch {
    return 'dev'
  }
}
